import { useState, type JSX } from "react";
import Chevron from "./Chevron";
import LedgerRow from "./LedgerRow";
import type { LedgerGroup as LedgerGroupModel, LedgerRow as LedgerRowModel } from "./buildLedger";

export interface LedgerGroupProps {
  group: LedgerGroupModel;
  checkedIds: string[];
  isDisabled: (row: LedgerRowModel) => boolean;
  disabledReason?: string;
  onToggle: (adoptId: string) => void;
}

/**
 * A category block: a fixed gutter carrying the label and tally, and the rows
 * beside it. A group with nothing to act on opens as a single summary button
 * so the modal stays short without concealing what the link contained.
 */
const LedgerGroup = ({
  group, checkedIds, isDisabled, disabledReason, onToggle,
}: LedgerGroupProps): JSX.Element => {
  const [expanded, setExpanded] = useState(!group.collapsedByDefault);

  const rows = group.rows.map((row) => (
    <LedgerRow
      key={row.id}
      row={row}
      checked={row.adoptId ? checkedIds.includes(row.adoptId) : false}
      disabled={isDisabled(row)}
      disabledReason={disabledReason}
      onToggle={onToggle}
    />
  ));

  return (
    <section
      aria-label={group.label}
      className="grid grid-cols-[100px_1fr] gap-4.5 items-start pt-3.5 mt-3.5 border-t border-neutral-200 first:mt-0 first:pt-0 first:border-t-0"
    >
      <div className="pt-[9px]">
        <div className="text-[11.5px] font-bold text-text-950 tracking-[-0.005em]">
          {group.label}
        </div>
        <div className="text-[11px] text-text-50 mt-0.5 tnum">{group.note}</div>
      </div>
      <div className="min-w-0">
        {group.collapsedByDefault && !expanded ? (
          <button
            type="button"
            aria-expanded={false}
            onClick={() => setExpanded(true)}
            className="flex items-center gap-[7px] text-[12.5px] text-text-50 py-2.5 pl-0.5 rounded-[4px] hover:text-text-300 cursor-pointer"
          >
            <Chevron open={false} />
            {group.summary}
          </button>
        ) : (
          rows
        )}
      </div>
    </section>
  );
};

export default LedgerGroup;
